// Heure du jeu dans le HUD : un petit cadran de 24 h (jour en haut, nuit en bas) où le soleil ou la lune tourne,
// et l'heure en clair. Cachée quand le cycle jour/nuit est désactivé (onglet Configuration).
import { gameConfig } from '../../shared/game-config.ts';
import { formatTimeOfDay, nightFactor, timeOfDay } from '../../shared/lighting.ts';

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Rayon du chemin du soleil et de la lune sur le cadran. */
const ORBIT = 6.5;

const svg = <K extends keyof SVGElementTagNameMap>(tag: K, attributes: Record<string, string | number>): SVGElementTagNameMap[K] => {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [name, value] of Object.entries(attributes)) node.setAttribute(name, String(value));
  return node;
};

export class GameClock {
  private readonly root: HTMLElement;
  private readonly body: SVGCircleElement;
  private readonly label: HTMLElement;
  /** Minute de jeu affichée : le DOM n'est touché que quand elle change. */
  private shown = -1;

  constructor(root: HTMLElement) {
    this.root = root;
    const dial = svg('svg', { viewBox: '-11 -11 22 22', width: 22, height: 22, 'aria-hidden': 'true', class: 'game-clock-dial' });
    // Moitié haute : le jour (midi en haut) ; moitié basse : la nuit (minuit en bas).
    dial.append(
      svg('path', { d: 'M -10 0 A 10 10 0 0 1 10 0 Z', class: 'game-clock-day' }),
      svg('path', { d: 'M 10 0 A 10 10 0 0 1 -10 0 Z', class: 'game-clock-night' }),
      svg('circle', { r: 10, class: 'game-clock-rim' }),
    );
    this.body = svg('circle', { r: 2.6 });
    dial.append(this.body);
    this.label = document.createElement('b');
    root.replaceChildren(dial, this.label);
  }

  /** À chaque image : rien n'est redessiné tant que la minute de jeu ne change pas. */
  update(now: number): void {
    const { dayNight } = gameConfig();
    this.root.hidden = !dayNight.enabled;
    if (!dayNight.enabled) return;
    const time = timeOfDay(dayNight, now);
    const minute = Math.floor(time * 24 * 60);
    if (minute === this.shown) return;
    this.shown = minute;
    const night = nightFactor(time) >= 0.5;
    // Minuit en bas, midi en haut ; le soleil se lève à gauche et se couche à droite.
    const angle = time * 2 * Math.PI;
    this.body.setAttribute('cx', (-Math.sin(angle) * ORBIT).toFixed(2));
    this.body.setAttribute('cy', (Math.cos(angle) * ORBIT).toFixed(2));
    this.body.setAttribute('class', night ? 'game-clock-moon' : 'game-clock-sun');
    this.label.textContent = formatTimeOfDay(time);
    this.root.title = `${night ? 'Nuit' : 'Jour'} — ${formatTimeOfDay(time)} (heure du jeu)`;
    this.root.classList.toggle('night', night);
  }
}
